// src/routes/AppRoutes.tsx
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Login } from "../pages/Login";
import { Home } from "../pages/Home";
import AppLayout from "../components/Layout/AppLayout";
import { Unauthorized } from "../pages/Unauthorized";
import ManageAuthorizedUsers from "../pages/ManageAuthorizedUsers";
import { useAuth } from "../context/AuthContext";
import { InvoiceDetail } from "../components/Invoice/InvoiceDetail";
import { InvoiceForm } from "../components/Invoice/InvoiceForm";
import { ItemsListWithDeleted } from "../components/ItemsAndInventory/ItemsListWithDeleted";
import { AddNewItem } from "../components/ItemsAndInventory/AddNewItem";
import { CreditInvoices } from "../components/Invoice/CreditInvoices";
import { LedgerSearch } from "../components/Reports/Ledger/LedgerSearch";
import { InvoiceAndReturnsSearch } from "../components/Reports/InvoiceAndReturns/InvoiceAndReturnsSearch";
import { ShopCash } from "../components/Reports/ShopCash/ShopCash";
import { AuditLog } from "../pages/AuditLog";
import { useDispatch } from "react-redux";
import { fetchItems } from "../redux/slices/itemsSlice";
import { AppDispatch } from "../redux/store";
import { PartnerInvestments } from "../components/Reports/PartnerInvestments/PartnerInvestments";
import { InvoiceList } from "../components/Invoice/InvoiceList";
import { NotFoundPage } from "../components/Shared/NotFoundPage";

export const AppRoutes = () => {
	const { user, loading, isAuthorized, authorizationError } = useAuth();
	const dispatch = useDispatch<AppDispatch>();

	useEffect(() => {
		if (isAuthorized) {
			dispatch(fetchItems());
		}
	}, [dispatch, isAuthorized]);

	if (loading) return <div>Loading...</div>; // Prevent redirect before auth is ready

	// If user is logged in but not authorized
	if (user && !isAuthorized) {
		return (
			<BrowserRouter>
				<Routes>
					<Route
						path="*"
						element={<Unauthorized error={authorizationError} />}
					/>
				</Routes>
			</BrowserRouter>
		);
	}

	return (
		<BrowserRouter>
			<Routes>
				<Route
					path="/login"
					element={
						isAuthorized ? <Navigate to="/" replace /> : <Login />
					}
				/>
				<Route
					path="/create-invoice"
					element={
						isAuthorized ? (
							<AppLayout>
								<InvoiceForm />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/invoice"
					element={
						isAuthorized ? (
							<AppLayout>
								<InvoiceDetail />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/search-invoice"
					element={
						isAuthorized ? (
							<AppLayout>
								<InvoiceList />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/items"
					element={
						isAuthorized ? (
							<AppLayout>
								<ItemsListWithDeleted />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/add-item"
					element={
						isAuthorized ? (
							<AppLayout>
								<AddNewItem />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/credit-invoices"
					element={
						isAuthorized ? (
							<AppLayout>
								<CreditInvoices />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/ledger"
					element={
						isAuthorized ? (
							<AppLayout>
								<LedgerSearch />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/invoice-and-returns"
					element={
						isAuthorized ? (
							<AppLayout>
								<InvoiceAndReturnsSearch />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/shop-cash"
					element={
						isAuthorized ? (
							<AppLayout>
								<ShopCash />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/investment-tracker"
					element={
						isAuthorized ? (
							<AppLayout>
								<PartnerInvestments />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/"
					element={
						isAuthorized ? (
							<AppLayout>
								<Home />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/audit-logs"
					element={
						isAuthorized ? (
							<AppLayout>
								<AuditLog />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="/manage-users"
					element={
						isAuthorized ? (
							<AppLayout>
								<ManageAuthorizedUsers />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
				<Route
					path="*"
					element={
						isAuthorized ? (
							<AppLayout>
								<NotFoundPage />
							</AppLayout>
						) : (
							<Navigate to="/login" />
						)
					}
				/>
			</Routes>
		</BrowserRouter>
	);
};
